import React from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import ContentCutOutlinedIcon from "@mui/icons-material/ContentCutOutlined";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { BUSINESS_THEMES, BUSINESS_TYPE_OPTIONS, BusinessTypeKey } from "../platform/config";

const TYPE_ICONS: Record<BusinessTypeKey, React.ElementType> = {
  CLINIC: LocalHospitalOutlinedIcon,
  SPA: SpaOutlinedIcon,
  SALON: ContentCutOutlinedIcon,
  BEAUTY: BrushOutlinedIcon,
  NEUTRAL: StorefrontOutlinedIcon,
};

/**
 * The kinds of business as cards, each in its own colours, so the owner sees
 * the look they are picking. Used at signup and in Settings.
 */
const BusinessTypePicker: React.FC<{
  value: BusinessTypeKey | null;
  onChange: (type: BusinessTypeKey) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => (
  <Box
    role="radiogroup"
    aria-label="Business type"
    sx={{ display: "grid", gap: 1.25, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}
  >
    {BUSINESS_TYPE_OPTIONS.map((option) => {
      const Icon = TYPE_ICONS[option.key];
      const colors = BUSINESS_THEMES[option.key];
      const selected = value === option.key;
      return (
        <ButtonBase
          key={option.key}
          role="radio"
          aria-checked={selected}
          disabled={disabled}
          onClick={() => onChange(option.key)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1.5,
            p: 1.5,
            textAlign: "left",
            borderRadius: 3,
            border: "2px solid",
            borderColor: selected ? colors.main : "#E3E6EE",
            bgcolor: selected ? colors.tint : "#fff",
            transition: "border-color 150ms, background-color 150ms",
            "&:hover": { borderColor: colors.main },
            "&.Mui-focusVisible": { outline: `3px solid ${colors.main}`, outlineOffset: 2 },
            // The last option ("Something else") spans the row.
            ...(option.key === "NEUTRAL" && { gridColumn: { sm: "1 / -1" } }),
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: colors.tint,
              color: colors.main,
            }}
          >
            <Icon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>{option.name}</Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{option.description}</Typography>
          </Box>
          {selected && <CheckCircleIcon sx={{ color: colors.main }} aria-hidden />}
        </ButtonBase>
      );
    })}
  </Box>
);

export default BusinessTypePicker;
